// covered-by: frontends/carrie/src/routes/ask-puntt/components/canvas/blocks/__tests__/VideoPreviewBlock.test.tsx
import {
  ArrowClockwise,
  CircleNotch,
  DownloadSimple,
  VideoCamera,
} from "@phosphor-icons/react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { useRef } from "react";

import { AssetDownloadButton } from "./AssetDownloadButton";

import { useHlsPlayer } from "../../../hooks/useHlsPlayer";

interface VideoPreviewBlockProps {
  url: string;
  fileName: string;
}

export function VideoPreviewBlock({ url, fileName }: VideoPreviewBlockProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { error, retry } = useHlsPlayer(videoRef, url);

  if (error) {
    return (
      <div
        className="flex items-center gap-3 rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-4"
        data-testid="video-preview-error"
      >
        <VideoCamera size={20} className="text-puntt-neutral-gray-9" />
        <div className="flex-1">
          <Text size="2" weight="medium" className="text-puntt-neutral-gray-12">
            {fileName}
          </Text>
          <Text as="p" size="1" className="text-puntt-neutral-gray-9">
            Preview unavailable
          </Text>
        </div>
        <Flex align="center" gap="2">
          <Button
            size="1"
            variant="soft"
            color="gray"
            onClick={retry}
            className="cursor-pointer"
            data-testid="video-preview-retry"
          >
            <ArrowClockwise size={14} />
            Retry
          </Button>
          <AssetDownloadButton
            url={url}
            fileName={fileName}
            testId="video-preview-error-download"
            ariaLabel={`Download ${fileName}`}
            className="text-white inline-flex items-center gap-1 rounded-md bg-puntt-accent-9 px-3 py-1.5 text-xs font-medium transition-colors hover:bg-puntt-accent-10 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {({ downloading }) => (
              <>
                {downloading ? (
                  <CircleNotch
                    size={14}
                    className="animate-spin"
                    aria-hidden="true"
                  />
                ) : (
                  <DownloadSimple size={14} aria-hidden="true" />
                )}
                Download
              </>
            )}
          </AssetDownloadButton>
        </Flex>
      </div>
    );
  }

  return (
    <div>
      <Flex align="center" gap="2" className="mb-2">
        <VideoCamera size={16} className="text-puntt-neutral-gray-9" />
        <Text size="2" weight="medium" className="text-puntt-neutral-gray-12">
          {fileName}
        </Text>
        <AssetDownloadButton
          url={url}
          fileName={fileName}
          testId="video-preview-download"
          ariaLabel={`Download ${fileName}`}
          title="Download video"
          className="ml-auto disabled:cursor-not-allowed disabled:opacity-60"
        >
          {({ downloading }) =>
            downloading ? (
              <CircleNotch
                size={14}
                className="animate-spin text-puntt-neutral-gray-9"
                aria-hidden="true"
              />
            ) : (
              <DownloadSimple
                size={14}
                className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                aria-hidden="true"
              />
            )
          }
        </AssetDownloadButton>
      </Flex>
      <div className="overflow-hidden rounded-lg border border-puntt-neutral-gray-6">
        <div className="relative max-h-[400px] bg-puntt-neutral-gray-3">
          {/* eslint-disable jsx-a11y/media-has-caption */}
          <video
            ref={videoRef}
            controls
            playsInline
            preload="metadata"
            className="mx-auto block max-h-[400px] w-full object-contain"
          />
        </div>
      </div>
    </div>
  );
}
